import React, { useEffect, useRef, useCallback, ReactNode, CSSProperties, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/cn';
import { ConfirmDialogContext } from '@/contexts/ConfirmDialogContext';

interface ModalCloseContextValue {
  closeHandler: () => void;
}

const ModalCloseContext = createContext<ModalCloseContextValue | null>(null);

/**
 * Hook for descendants of <Modal> to retrieve the guarded close handler
 * (respects the Modal's `isDirty` prop). Returns a no-op when used outside
 * a Modal.
 */
export function useModalClose(): () => void {
  const ctx = useContext(ModalCloseContext);
  return ctx?.closeHandler ?? (() => {});
}

// ============================================================================
// Modal Stack Management (for automatic z-index stacking)
// ============================================================================

const Z_INDEX_BASE = 9999;
const Z_INDEX_STEP = 10;
let openModalCount = 0;

/**
 * Reset the internal modal stack counter.
 * Exported only for use in tests.
 */
export function resetModalCount(): void {
  openModalCount = 0;
}

/**
 * Z-index for content portaled to document.body (e.g. the Select dropdown)
 * that must render above the topmost open modal regardless of stack depth.
 * With no modal open this matches the historical 10001 baseline.
 */
export function getPortalZIndex(): number {
  return Z_INDEX_BASE + openModalCount * Z_INDEX_STEP + 2;
}

// ============================================================================
// Types and Interfaces
// ============================================================================

export type ModalChildren = ReactNode | ((api: { close: () => void }) => ReactNode);

export interface ModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose?: () => void;
  /**
   * Children content. Can be ReactNode or a function receiving { close }
   * with the close-guard applied — useful for Cancel buttons that should
   * respect the isDirty confirmation.
   */
  children: ModalChildren;
  /** Modal size preset */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Whether to show close button */
  showCloseButton?: boolean;
  /** Whether clicking backdrop closes modal */
  closeOnBackdrop?: boolean;
  /** Whether pressing escape closes modal */
  closeOnEscape?: boolean;
  /** Custom class for modal container */
  className?: string;
  /** Custom class for backdrop */
  backdropClassName?: string;
  /** Custom class for content wrapper */
  contentClassName?: string;
  /** Animation type */
  animation?: 'fade' | 'slide' | 'scale' | 'none';
  /** Position of modal */
  position?: 'center' | 'top' | 'bottom';
  /** Whether to trap focus within modal */
  trapFocus?: boolean;
  /** Whether to lock body scroll when open */
  lockScroll?: boolean;
  /** Z-index for modal */
  zIndex?: number;
  /** Custom styles */
  style?: CSSProperties;
  /** ARIA label for accessibility */
  ariaLabel?: string;
  /** ARIA description for accessibility */
  ariaDescription?: string;
  /**
   * When returns true, intercept close attempts (backdrop click, escape key,
   * X button) and show a confirmation dialog before actually closing.
   * Requires ConfirmDialogProvider in the tree.
   */
  isDirty?: () => boolean;
  /** Custom message for the unsaved-changes confirmation dialog */
  dirtyConfirmMessage?: string;
  /** Callback when modal opens */
  onOpen?: () => void;
  /** Callback after open animation */
  onOpenComplete?: () => void;
  /** Callback after close animation */
  onCloseComplete?: () => void;
  /** Portal container (defaults to document.body) */
  portalContainer?: HTMLElement;
}

export interface ModalHeaderProps {
  children: ReactNode;
  className?: string;
  onClose?: () => void;
  showCloseButton?: boolean;
}

export interface ModalBodyProps {
  children: ReactNode;
  className?: string;
}

export interface ModalFooterProps {
  children: ReactNode;
  className?: string;
}

// ============================================================================
// Size and Position Configurations
// ============================================================================

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'max-w-full mx-4',
};

const positionClasses = {
  center: 'items-center justify-center',
  top: 'items-start justify-center pt-16',
  bottom: 'items-end justify-center pb-16',
};

const animationClasses = {
  fade: {
    base: 'transition-opacity duration-300',
    enter: 'opacity-0',
    enterActive: 'opacity-100',
    exit: 'opacity-100',
    exitActive: 'opacity-0',
  },
  slide: {
    base: 'transition-transform duration-300',
    enter: 'translate-y-full',
    enterActive: 'translate-y-0',
    exit: 'translate-y-0',
    exitActive: 'translate-y-full',
  },
  scale: {
    base: 'transition-[transform,opacity] duration-300',
    enter: 'scale-95 opacity-0',
    enterActive: 'scale-100 opacity-100',
    exit: 'scale-100 opacity-100',
    exitActive: 'scale-95 opacity-0',
  },
  none: {
    base: '',
    enter: '',
    enterActive: '',
    exit: '',
    exitActive: '',
  },
};

// ============================================================================
// Main Modal Component
// ============================================================================

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  size = 'md',
  showCloseButton: _showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  className,
  backdropClassName,
  contentClassName,
  animation = 'fade',
  position = 'center',
  trapFocus = true,
  lockScroll = true,
  zIndex: zIndexProp,
  style,
  ariaLabel,
  ariaDescription,
  isDirty,
  dirtyConfirmMessage = 'Você tem alterações não salvas. Deseja descartar?',
  onOpen,
  onOpenComplete,
  onCloseComplete,
  portalContainer,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const animationTimeoutRef = useRef<NodeJS.Timeout>();
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);
  const [effectiveZIndex, setEffectiveZIndex] = React.useState(zIndexProp ?? Z_INDEX_BASE);

  const confirmDialog = useContext(ConfirmDialogContext);
  const isGuardingRef = useRef(false);

  const closeHandler = useCallback(async () => {
    if (!onClose) return;
    if (isGuardingRef.current) return;

    if (!isDirty || !isDirty()) {
      onClose();
      return;
    }

    if (!confirmDialog) {
      onClose();
      return;
    }

    isGuardingRef.current = true;
    try {
      const confirmed = await confirmDialog.showConfirm({
        title: 'Descartar alterações?',
        message: dirtyConfirmMessage,
        type: 'warning',
        confirmText: 'Descartar',
        cancelText: 'Continuar editando',
      });
      if (confirmed) {
        onClose();
      }
    } finally {
      isGuardingRef.current = false;
    }
  }, [onClose, isDirty, confirmDialog, dirtyConfirmMessage]);

  // Manage modal stack level for automatic z-index stacking
  useEffect(() => {
    if (isOpen) {
      if (zIndexProp === undefined) {
        // Auto-calculate z-index based on how many modals are already open
        const level = openModalCount;
        setEffectiveZIndex(Z_INDEX_BASE + (level * Z_INDEX_STEP));
      } else {
        setEffectiveZIndex(zIndexProp);
      }
      openModalCount++;
    }

    return () => {
      if (isOpen) {
        openModalCount = Math.max(0, openModalCount - 1);
      }
    };
  }, [isOpen, zIndexProp]);

  // Handle opening
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsAnimating(true);
      onOpen?.();

      // Store previous focus
      previousFocusRef.current = document.activeElement as HTMLElement;

      // Set initial focus after animation
      animationTimeoutRef.current = setTimeout(() => {
        setIsAnimating(false);
        onOpenComplete?.();

        // Focus first focusable element
        if (trapFocus && modalRef.current) {
          const focusableElements = modalRef.current.querySelectorAll(
            'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
          );
          const firstElement = focusableElements[0] as HTMLElement;
          firstElement?.focus();
        }
      }, 300);
    } else if (isVisible) {
      setIsAnimating(true);
      animationTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        setIsAnimating(false);
        onCloseComplete?.();

        // Restore previous focus
        if (previousFocusRef.current) {
          previousFocusRef.current.focus();
          previousFocusRef.current = null;
        }
      }, 300);
    }

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [isOpen, isVisible, trapFocus, onOpen, onOpenComplete, onCloseComplete]);

  // Handle escape key
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeHandler();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, closeHandler]);

  // Handle focus trap
  useEffect(() => {
    if (!trapFocus || !isOpen || !isVisible) return;

    const node = modalRef.current;
    if (!node) return;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusableElements = node.querySelectorAll(
        'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements.length === 0) return;
      
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen, trapFocus, isVisible]);

  // Handle body scroll lock - only restore scroll when ALL modals are closed
  useEffect(() => {
    if (!lockScroll || !isOpen) return;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Only capture original styles if this is the first modal locking scroll
    const isFirstLockingModal = openModalCount <= 1;
    const savedOverflow = isFirstLockingModal ? document.body.style.overflow : undefined;
    const savedPaddingRight = isFirstLockingModal ? document.body.style.paddingRight : undefined;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      // Only restore scroll if no other modals are still open
      // openModalCount has already been decremented for this modal by the stack effect
      if (openModalCount <= 0) {
        document.body.style.overflow = savedOverflow ?? '';
        document.body.style.paddingRight = savedPaddingRight ?? '';
      }
    };
  }, [isOpen, lockScroll]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!closeOnBackdrop) return;
    const target = e.target as Node;
    const contentEl = modalRef.current;
    if (contentEl && !contentEl.contains(target)) {
      closeHandler();
    }
  }, [closeOnBackdrop, closeHandler]);

  if (!isVisible) return null;

  const animationClass = animationClasses[animation];
  const isEntering = isOpen && isAnimating;
  const isExiting = !isOpen && isAnimating;

  const backdropAnimationClass = cn(
    'fixed inset-0 bg-black/50',
    animationClass.base,
    {
      [animationClass.enter]: isEntering,
      [animationClass.enterActive]: isOpen && !isAnimating,
      [animationClass.exitActive]: isExiting,
    },
    backdropClassName
  );

  const modalAnimationClass = cn(
    'relative bg-white rounded-lg shadow-xl',
    sizeClasses[size],
    animationClass.base,
    {
      [animationClass.enter]: isEntering,
      [animationClass.enterActive]: isOpen && !isAnimating,
      [animationClass.exitActive]: isExiting,
    },
    contentClassName
  );

  const modalContent = (
    <>
      {/* Backdrop */}
      <div
        className={backdropAnimationClass}
        style={{ zIndex: effectiveZIndex }}
        aria-hidden="true"
        data-modal-backdrop=""
        onClick={() => {
          if (closeOnBackdrop) closeHandler();
        }}
      />

      {/* Modal Container */}
      <div
        className={cn(
          'fixed inset-0 overflow-y-auto',
          className
        )}
        style={{ zIndex: effectiveZIndex + 1, ...style }}
        onClick={handleBackdropClick}
      >
        <div className={cn('flex min-h-full p-4', positionClasses[position])}>
          {/* Modal Content */}
          <div
            ref={modalRef}
            className={cn(modalAnimationClass, 'flex flex-col max-h-[90vh]')}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            aria-describedby={ariaDescription}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalCloseContext.Provider value={{ closeHandler }}>
              {typeof children === 'function' ? children({ close: closeHandler }) : children}
            </ModalCloseContext.Provider>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, portalContainer || document.body);
};

// ============================================================================
// Modal Sub-components
// ============================================================================

export const ModalHeader: React.FC<ModalHeaderProps> = ({
  children,
  className,
  onClose,
  showCloseButton = true,
}) => {
  const ctx = useContext(ModalCloseContext);
  const effectiveClose = ctx?.closeHandler ?? onClose;
  return (
    <div className={cn('flex items-center justify-between p-6 border-b flex-shrink-0', className)}>
      <div className="flex-1">{children}</div>
      {showCloseButton && effectiveClose && (
        <button
          type="button"
          onClick={effectiveClose}
          className="ml-4 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-lg p-2 min-h-[40px] min-w-[40px] flex items-center justify-center"
          aria-label="Close modal"
        >
          <svg
            className="h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export const ModalBody: React.FC<ModalBodyProps> = ({ children, className }) => {
  return (
    <div className={cn('p-6 overflow-y-auto flex-1', className)}>
      {children}
    </div>
  );
};

export const ModalFooter: React.FC<ModalFooterProps> = ({ children, className }) => {
  return (
    <div className={cn('flex justify-end gap-3 p-6 border-t flex-shrink-0', className)}>
      {children}
    </div>
  );
};

// ============================================================================
// Specialized Modal Components
// ============================================================================

export interface ConfirmModalProps extends Omit<ModalProps, 'children'> {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  type?: 'danger' | 'warning' | 'info' | 'success';
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  type = 'info',
  ...modalProps
}) => {
  const typeColors = {
    danger: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    warning: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
    info: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
    success: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
  };

  const typeIcons = {
    danger: (
      <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    warning: (
      <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    info: (
      <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    success: (
      <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  };

  const handleCancel = () => {
    onCancel?.();
    onClose?.();
  };

  const handleConfirm = () => {
    onConfirm();
    onClose?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      {...modalProps}
    >
      <ModalBody>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            {typeIcons[type]}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {title}
            </h3>
            <p className="text-sm text-gray-600">
              {message}
            </p>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          onClick={handleCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {cancelText}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          className={cn(
            'px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2',
            typeColors[type]
          )}
        >
          {confirmText}
        </button>
      </ModalFooter>
    </Modal>
  );
};

// ============================================================================
// Alert Modal Component (Simple notification dialog)
// ============================================================================

export interface AlertModalProps extends Omit<ModalProps, 'children'> {
  title: string;
  message: string;
  buttonText?: string;
  type?: 'info' | 'success' | 'warning' | 'danger';
}

export const AlertModal: React.FC<AlertModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  buttonText = 'OK',
  type = 'info',
  ...modalProps
}) => {
  const typeColors = {
    danger: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    warning: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
    info: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
    success: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
  };

  const typeIcons = {
    danger: (
      <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    warning: (
      <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    info: (
      <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    success: (
      <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      {...modalProps}
    >
      <ModalBody>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            {typeIcons[type]}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {title}
            </h3>
            <p className="text-sm text-gray-600">
              {message}
            </p>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          onClick={onClose}
          className={cn(
            'px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2',
            typeColors[type]
          )}
        >
          {buttonText}
        </button>
      </ModalFooter>
    </Modal>
  );
};

// ============================================================================
// Prompt Modal Component (Text input dialog)
// ============================================================================

export interface PromptModalProps extends Omit<ModalProps, 'children'> {
  title: string;
  message: string;
  defaultValue?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
  inputType?: 'text' | 'number' | 'email' | 'url';
  onConfirm: (value: string) => void;
  onCancel?: () => void;
}

export const PromptModal: React.FC<PromptModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  defaultValue = '',
  placeholder = '',
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  inputType = 'text',
  onConfirm,
  onCancel,
  ...modalProps
}) => {
  const [value, setValue] = React.useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isOpen) {
      setValue(defaultValue);
      // Focus the input after the modal animation
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 350);
    }
  }, [isOpen, defaultValue]);

  const handleCancel = () => {
    onCancel?.();
    onClose?.();
  };

  const handleConfirm = () => {
    onConfirm(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      {...modalProps}
    >
      <ModalBody>
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {title}
            </h3>
            <p className="text-sm text-gray-600">
              {message}
            </p>
          </div>
          <input
            ref={inputRef}
            type={inputType}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          onClick={handleCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {cancelText}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {confirmText}
        </button>
      </ModalFooter>
    </Modal>
  );
};

// ============================================================================
// Drawer Component (Slide-out Modal Variant)
// ============================================================================

export interface DrawerProps extends Omit<ModalProps, 'position' | 'animation'> {
  position?: 'left' | 'right' | 'top' | 'bottom';
}

export const Drawer: React.FC<DrawerProps> = ({
  position = 'right',
  children,
  contentClassName,
  ...modalProps
}) => {
  const positionClasses = {
    left: 'left-0 top-0 h-full',
    right: 'right-0 top-0 h-full',
    top: 'top-0 left-0 w-full',
    bottom: 'bottom-0 left-0 w-full',
  };

  return (
    <Modal
      {...modalProps}
      animation="none"
      contentClassName={cn(
        'fixed',
        positionClasses[position],
        'bg-white shadow-xl',
        contentClassName
      )}
    >
      {children}
    </Modal>
  );
};

// ============================================================================
// Default Export
// ============================================================================

export default Modal;
